import React, { useState } from "react";
import { Key } from "../../../models/Key";

interface Props {
  keys: Key[];
}

const AuthenticationDocs: React.FC<Props> = ({ keys }) => {
  const [copied, setCopied] = useState(false);
  const activeKey = keys?.[0];

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white border border-gallery-200 rounded-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gallery-100">
        <h2 className="font-display text-lg text-gallery-900">Authentication</h2>
      </div>

      <div className="p-6 space-y-6">
        {/* Base URL */}
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-2">
            Base URL
          </h3>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-4 py-2.5 bg-gallery-50 border border-gallery-200 rounded-sm text-sm font-mono text-gallery-800">
              https://partner-api.artcollection.io/api/v1
            </code>
            <button
              onClick={() => copyToClipboard("https://partner-api.artcollection.io/api/v1")}
              className="p-2.5 text-gallery-500 hover:text-gallery-700 hover:bg-gallery-100 rounded-sm transition-colors"
              title="Copy to clipboard"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Auth Header */}
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-2">
            Authorization Header
          </h3>
          <p className="text-sm text-gallery-600 mb-3">
            Include your API key in the Authorization header with every request:
          </p>

          {activeKey ? (
            <div className="relative">
              <div className="px-4 py-3 bg-gallery-900 rounded-sm font-mono text-sm overflow-x-auto">
                <span className="text-gallery-400">Authorization: </span>
                <span className="text-white">Bearer {activeKey.token}</span>
              </div>
              <button
                onClick={() => copyToClipboard(`Authorization: Bearer ${activeKey.token}`)}
                className="absolute top-2 right-2 p-1.5 text-gallery-400 hover:text-white hover:bg-gallery-700 rounded transition-colors"
                title="Copy to clipboard"
              >
                {copied ? (
                  <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          ) : (
            <div className="px-4 py-3 bg-gallery-100 border border-gallery-200 rounded-sm">
              <p className="text-sm text-gallery-600">
                No API key found.{" "}
                <a href="/keys" className="text-gallery-900 font-medium hover:underline">
                  Generate an API key
                </a>{" "}
                to get started.
              </p>
            </div>
          )}
        </div>

        {/* Example Request */}
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-2">
            Example Request
          </h3>
          <div className="bg-gallery-900 rounded-sm p-4 overflow-x-auto">
            <pre className="text-sm font-mono text-gallery-100">
              <span className="text-emerald-400">curl</span>
              <span className="text-white"> -X GET \</span>
              {"\n"}
              <span className="text-white">  "https://partner-api.artcollection.io/api/v1/artists?name=Warhol" \</span>
              {"\n"}
              <span className="text-white">  -H </span>
              <span className="text-amber-300">"Authorization: Bearer YOUR_API_KEY"</span>
            </pre>
          </div>
        </div>

        {/* Response Codes */}
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-3">
            Response Codes
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { code: "200", label: "Success", color: "text-emerald-700 bg-emerald-50" },
              { code: "400", label: "Bad Request", color: "text-amber-700 bg-amber-50" },
              { code: "401", label: "Unauthorized", color: "text-rose-700 bg-rose-50" },
              { code: "429", label: "Rate Limited", color: "text-orange-700 bg-orange-50" },
            ].map((item) => (
              <div key={item.code} className="flex items-center gap-3 px-3 py-2 border border-gallery-200 rounded-sm">
                <span className={`px-2 py-0.5 text-xs font-mono font-medium rounded ${item.color}`}>
                  {item.code}
                </span>
                <span className="text-sm text-gallery-600">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationDocs;
